/**
 * @file Renders the list of unresolved investment errors with a resolve action.
 */
import { Alert, List, Typography, Space, Row, Col } from "antd";

const LABEL_RESOLVE = "Resolve";

const ALERT_STYLE = { padding: "4px 10px" };
const WRAPPER_STYLE = { marginTop: 4, marginBottom: 4 };
const ITEM_STYLE = { padding: "1px 0px 1px 0px", border: "none" };

type ErrorItem = {
  key: number;
  text: string;
};

type Props = {
  errors?: string[];
  isFunded: boolean;
  onResolve: () => void;
};

/**
 * Renders the investment's unresolved errors as an alert, unless the investment is funded.
 */
const InvestmentErrors = ({
  errors = [],
  isFunded,
  onResolve,
}: Props) => {
  const dataSource: ErrorItem[] = errors
    .map((text, key) => ({ key, text }));

  const hasErrors = dataSource.length > 0;

  if (!hasErrors) {
    return null;
  }

  if (isFunded) {
    return null;
  }

  /**
   * Renders a single error entry as a bulleted list item.
   */
  const renderItem = (item: ErrorItem) =>
    <List.Item style={ITEM_STYLE}>
      <Space>
        <Typography.Text strong>&bull;</Typography.Text>
        <Typography.Text>{item.text}</Typography.Text>
      </Space>
    </List.Item>;

  const description = (
    <Row>
      <Col flex="auto">
        <List
          size="small"
          itemLayout="vertical"
          dataSource={dataSource}
          renderItem={renderItem}
        />
      </Col>
      <Col>
        <Typography.Link onClick={onResolve}>
          {LABEL_RESOLVE}
        </Typography.Link>
      </Col>
    </Row>
  );

  return (
    <div style={WRAPPER_STYLE}>
      <Alert
        type="error"
        style={ALERT_STYLE}
        description={description}
      />
    </div>
  );
};

export default InvestmentErrors;
